import React, { useEffect, useMemo } from 'react';
import TravelRouteCard from 'v3/components/TravelRouteCard';
import {
  insertWaypointsIntoPoints,
  normalizeWaypoints,
} from 'v3/components/TravelRouteCard/utils/points';
import { getRouteWaypointsFromPoints } from 'v3/components/TravelRouteCard/utils/routeMachineCard';
import { useInsuranceValue } from 'contexts/InsuranceValueContext';
import { TravelContext } from '../controller';

const [, useTravel] = TravelContext;

export default function LocaleData({
  freightValuesRef,
  freightValuesReplaceRef
}) {
  const travel = useTravel();
  const { updateInsuranceValue } = useInsuranceValue();

  const points = useMemo(() => {
    const pts = [];

    if (travel?.data?.origin?.lat && travel?.data?.origin?.lng) {
      pts.push(travel?.data?.origin);
    }

    if (travel?.data?.destinations?.length > 0) {
      pts.push(...travel?.data?.destinations);
    }

    const waypoints = normalizeWaypoints(
      travel?.data?.polylineWaypoints || travel?.data?.polyline_waypoints,
    );
    return insertWaypointsIntoPoints(pts, waypoints, {
      defaultCountry: { id: 1, name: 'Brasil', abbreviation: 'br', ddi: 55 },
    });
  }, [
    travel?.data?.origin,
    travel?.data?.destinations,
    travel?.data?.polylineWaypoints,
    travel?.data?.polyline_waypoints,
  ]);

  async function onChangeAddress({ origin, destinations, polyline, polylineWaypoints }) {
    travel.setData(prev => ({
      ...prev,
      origin: origin || null,
      destinations: destinations || [],
      polylineRoute: polyline || prev?.polylineRoute || prev?.polyline_route || null,
      polyline_route: polyline || prev?.polylineRoute || prev?.polyline_route || null,
      polylineWaypoints:
        polylineWaypoints === undefined
          ? prev.polylineWaypoints
          : polylineWaypoints,
      polyline_waypoints:
        polylineWaypoints === undefined
          ? prev.polyline_waypoints
          : polylineWaypoints,
    }));

    const lastDestination = destinations?.length ? destinations[destinations?.length - 1] : null;

    const originProps = {
      originIbgeCode: origin?.city?.ibge_code || origin?.city?.ibgeCode,
      originState: origin?.city?.province?.uf,
      originCityName: origin?.city?.name,
      originStateName: origin?.city?.province?.name,
      originCountry: origin?.countrySlug,
    };

    const destinationProps = {
      destinationIbgeCode: lastDestination?.city?.ibge_code || lastDestination?.city?.ibgeCode,
      destinationCityName: lastDestination?.city?.name,
      destinationStateName: lastDestination?.city?.province?.name,
      destinationState: lastDestination?.city?.province?.uf,
      destinationCountry: lastDestination?.countrySlug,
    };

    const insuranceValue = await updateInsuranceValue({
      document: travel?.data?.client?.cpfCnpj,
      loadValue: travel?.data?.cargo_value,
      currency: travel?.data?.currency?.value,
      exchange: travel?.data?.exchange,
      container: travel?.data?.container,
      vehicleId: travel?.data?.vehicle?.main?.id,
      ...originProps,
      ...destinationProps,
    });

    if (insuranceValue !== null && insuranceValue !== undefined) {
      await freightValuesRef?.current?.setInsuranceValueBenner(insuranceValue);
      await freightValuesReplaceRef?.current?.setInsuranceValueBenner(insuranceValue);
    }
  }

  function onChangeRoute({ polyline, polylineWaypoints }) {
    travel.setData(prev => {
      const nextPolyline = polyline || null;
      const nextWaypoints =
        polylineWaypoints === undefined
          ? prev.polylineWaypoints
          : polylineWaypoints;

      const samePolyline =
        (prev?.polylineRoute || prev?.polyline_route || null) === nextPolyline;
      const sameWaypoints =
        JSON.stringify(prev?.polylineWaypoints || prev?.polyline_waypoints || null) ===
        JSON.stringify(nextWaypoints || null);

      if (samePolyline && sameWaypoints) {
        return prev;
      }

      return {
        ...prev,
        polylineRoute: nextPolyline,
        polyline_route: nextPolyline,
        polylineWaypoints: nextWaypoints,
        polyline_waypoints: nextWaypoints,
      };
    });
  }

  function onSaveMachineRoute(updatedPoints = [], { polylineRoute, totalDistanceInKm } = {}) {
    if (!Array.isArray(updatedPoints) || updatedPoints.length === 0) {
      travel.setData(prev => ({
        ...prev,
        origin: null,
        destinations: [],
        polylineRoute: null,
        polyline_route: null,
        polylineWaypoints: null,
        polyline_waypoints: null,
        totalDistanceInKm: 0,
      }));
      return;
    }

    const normalizedPoints = updatedPoints.filter(Boolean);
    const businessPoints = normalizedPoints.filter(
      point => point?.type?.id !== 'via' && point?.action?.id !== 'via',
    );

    if (businessPoints.length < 2) {
      travel.setData(prev => ({
        ...prev,
        origin: businessPoints[0] || null,
        destinations: [],
        polylineRoute: null,
        polyline_route: null,
        polylineWaypoints: null,
        polyline_waypoints: null,
        totalDistanceInKm: 0,
      }));
      return;
    }

    const [origin, ...destinations] = businessPoints;

    const nextWaypoints = getRouteWaypointsFromPoints(normalizedPoints);
    const nextPolyline = polylineRoute || null;

    travel.setData(prev => {
      const sameOrigin =
        JSON.stringify(prev?.origin || null) === JSON.stringify(origin || null);
      const sameDestinations =
        JSON.stringify(prev?.destinations || []) === JSON.stringify(destinations || []);
      const samePolyline =
        (prev?.polylineRoute || prev?.polyline_route || null) === nextPolyline;
      const sameWaypoints =
        JSON.stringify(prev?.polylineWaypoints || prev?.polyline_waypoints || null) ===
        JSON.stringify(nextWaypoints);

      if (sameOrigin && sameDestinations && samePolyline && sameWaypoints) {
        return prev;
      }

      return {
        ...prev,
        origin: origin || null,
        destinations: destinations || [],
        polylineRoute: nextPolyline,
        polyline_route: nextPolyline,
        polylineWaypoints: nextWaypoints,
        polyline_waypoints: nextWaypoints,
        totalDistanceInKm: totalDistanceInKm || 0,
      };
    });
  }

  const validateRetroactive = useMemo(() => {
    if (!travel?.data?.costCenter) return false;
    return travel.data?.costCenter?.limitSchedule;
  }, [travel?.data?.costCenter]);

  const hasOrigin = !!(points?.[0]?.lat && points?.[0]?.lng);
  const hasDestination = points.slice(1).some(
    d =>
      typeof d?.lat === 'number' &&
      !Number.isNaN(d.lat) &&
      typeof d?.lng === 'number' &&
      !Number.isNaN(d.lng),
  );

  const showRouteErrors = !!(travel?.errors?.origin || travel?.errors?.destination);

  const routeErrorMessage =
    !hasOrigin && hasDestination
      ? travel?.errors?.origin || ''
      : hasOrigin && !hasDestination
      ? travel?.errors?.destination || ''
      : '';

  useEffect(() => {
    if (showRouteErrors && hasOrigin && hasDestination && travel.setErrors) {
      travel.setErrors(prev => ({
        ...prev,
        origin: undefined,
        destination: undefined,
      }));
    }
  }, [showRouteErrors, hasOrigin, hasDestination, travel.setErrors]);

  return (
    <>
      <div style={{ marginTop: '1rem' }}>
        <TravelRouteCard
          points={points}
          externalLoading={travel?.isLoading}
          showRouteErrors={showRouteErrors}
          routeErrorMessage={routeErrorMessage}
          travelData={travel?.data}
          takerValue={travel?.data?.takerValue}
          tollsPlazaInfos={travel?.tollsPlazaInfos}
          setTollsPlazaInfos={travel?.setTollsPlazaInfos}
          externalTotalDistanceInKm={travel?.data?.totalDistanceInKm || 0}
          externalPolylineRoute={travel?.data?.polylineRoute || travel?.data?.polyline_route || null}
          alwaysEditable
          onChangeAddress={onChangeAddress}
          onChangeRoute={onChangeRoute}
          onSavePoints={onSaveMachineRoute}
        />
      </div>
    </>
  );
}
